import React, { useEffect } from "react";
import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import { OPEN_CANCEL_SELL_ORDER_MODAL } from "@constants/actionTypes";
import { defaultAction } from "@actions/defaultAction";
import { cancelSellOrderWithReload } from "@actions/sellActions";
import loadable from "@loadable/component";
const CancelSellOrderModal = loadable(() =>
  import(
    /* webpackChunkName: "CancelSellOrderModal" */ "./CancelSellOrderModal"
  )
);
import { useDispatch, useSelector } from "react-redux";

const MainContainer = styled.div`
  min-height: 800px;
  mix-blend-mode: luminosity;
  background-image: linear-gradient(
    to top,
    rgba(34, 172, 256, 0) 96%,
    rgba(34, 172, 256, 1) -64%
  );
  padding-top: 200px;
`;

const InnerContainer = styled.div`
  width: 700px;
  border-radius: 30px;
  box-shadow: 0 50px 80px 0 rgba(51, 67, 61, 0.1),
    0 2px 4px 0 rgba(51, 67, 61, 0.05);
  background-color: #f8f8f8;
  margin: 0 auto;
  margin-bottom: 50px;
  @media (max-width: 900px) {
    width: 80%;
  }
  @media (max-width: 710px) {
    width: 90%;
  }
  @media (max-width: 340px) {
    width: 95%;
  }
`;

const TopSection = styled.div`
  text-align: center;
  padding-top: 50px;
`;

const Header = styled.p`
  font-size: 30px;
  font-weight: 500;
  line-height: 1.4;
  text-align: center;
  color: #1f2021;
  @media (max-width: 500px) {
    font-size: 27px;
  }
`;

const DateP = styled.p`
  font-size: 16px;
  line-height: 1.13;
  text-align: center;
  color: #777984;
  margin-bottom: 30px;
`;

const BottomSection = styled.div`
  background-color: #ffffff;
  border-radius: 30px;
`;

const CustomerInfo = styled.div`
  box-shadow: 0 5px 20px 0 rgba(31, 32, 33, 0.05);
  padding: 30px 40px 20px 40px;
  @media (max-width: 625px) {
    padding: 30px 20px 20px 20px;
  }
`;

const SmallHeader = styled.p`
  font-size: 18px;
  font-weight: bold;
  color: #1f2021;
  margin: 0;
`;

const Text = styled.p`
  font-size: 14px;
  line-height: 1.3;
  margin: 0;
  color: #777984;
  margin-bottom: 10px;
`;

const StatusInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  box-shadow: 0 5px 20px 0 rgba(31, 32, 33, 0.05);
  background-color: #ffffff;
  padding: 30px 40px 20px 40px;
  align-items: center;

  @media (max-width: 1000px) {
    grid-template-columns: 0.8fr 1fr;
  }
  @media (max-width: 750px) {
    grid-template-columns: 0.7fr 1fr;
  }
  @media (max-width: 625px) {
    grid-template-columns: 0.3fr 1fr;
    padding: 20px 20px 20px 20px;
  }
  @media (max-width: 500px) {
    display: block;
  }
`;

const StatusGrid = styled.div`
  @media (max-width: 500px) {
    margin-top: 17px;
  }
`;

const PendingDiv = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr;
  grid-gap: 10px;
  @media (max-width: 500px) {
    display: flex;
    justify-content: flex-start;
  }
`;

const StatusText = styled.span`
  background-color: rgba(255, 156, 27, 0.1);
  color: #ff9c1b;
  font-size: 14px;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 109px;
  text-align: center;
  height: 48px;
  border-radius: 30px;
  padding-right: 10px;
  padding-left: 10px;
  justify-self: flex-start;

  @media (max-width: 500px) {
    margin-right: 15px;
  }
`;

const StatusMessage = styled.span`
  font-size: 12px;
  color: #777984;
  justify-self: flex-end;
`;

const InfoImage = styled.img`
  margin-right: 7px;
`;

const CustomerDiv = styled.div`
  margin-top: 15px;
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 1000px) {
    grid-template-columns: 0.8fr 1fr;
  }
  @media (max-width: 750px) {
    grid-template-columns: 0.7fr 1fr;
  }
  @media (max-width: 500px) {
    display: block;
  }
`;

const CardSection = styled.section`
  border-radius: 0px 0px 30px 30px;
  box-shadow: 0 5px 20px 0 rgba(31, 32, 33, 0.05);
  background-color: #ffffff;
  padding: 30px 40px 20px 40px;
  @media (max-width: 625px) {
    padding: 30px 20px 20px 20px;
  }
`;

const Card = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 2fr 1fr 1fr;
  font-size: 16px;
  color: #1f2021;

  @media (max-width: 1000px) {
    grid-template-columns: 1.7fr 1fr 1fr;
  }
  @media (max-width: 750px) {
    grid-template-columns: 1.5fr 1fr 1fr;
  }
  @media (max-width: 565px) {
    grid-template-columns: 2.5fr 1fr 1fr;
  }
  @media (max-width: 365px) {
    grid-template-columns: 2.5fr 1.3fr 0.8fr;
  }
`;

const PaymentTypeGrid = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 2fr 1fr 1fr;
  font-size: 16px;
  color: #1f2021;

  @media (max-width: 1000px) {
    grid-template-columns: 1.7fr 1fr 1fr;
  }
  @media (max-width: 750px) {
    grid-template-columns: 1.5fr 1fr 1fr;
  }
  @media (max-width: 565px) {
    grid-template-columns: 2.5fr 1fr 1fr;
  }
  @media (max-width: 450px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
  @media (max-width: 400px) {
    grid-template-columns: 1fr auto auto;
    grid-gap: 30px;
  }
`;

const PaymentGrid = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 0.8fr 1fr 0.4fr;
  font-size: 16px;
  color: #1f2021;

  @media (max-width: 600px) {
    grid-template-columns: 0.6fr 1fr 0.4fr;
  }
  @media (max-width: 470px) {
    grid-template-columns: 0.3fr 1fr 0.4fr;
  }
  @media (max-width: 440px) {
    grid-template-columns: 0fr 1fr 0.4fr;
  }
`;

const CardImageNameDiv = styled.div`
  height: 100%;
  width: 100%;
  padding-bottom: 20px;
  padding-top: 20px;
  border-bottom: ${props => props.borderBottom};

  @media (max-width: 600px) {
    font-size: 14px;
  }
`;

const CardImage = styled.img`
  width: 56px;
  height: 36px;
  margin-right: 25px;

  @media (max-width: 700px) {
    margin-right: 15px;
  }
`;

const Facevalue = styled.div`
  justify-self: flex-start;
  height: 100%;
  width: 100%;
  padding-bottom: 20px;
  padding-top: 20px;
  display: flex;
  align-items: center;
  border-bottom: ${props => props.borderBottom};

  @media (max-width: 565px) {
    font-size: 14px;
  }
  @media (max-width: 365px) {
    font-size: 12px;
  }
`;

const OfferValue = styled.div`
  font-weight: bold;
  justify-self: flex-end;
  border-bottom: ${props => props.borderBottom};
  height: 100%;
  width: 100%;
  padding-bottom: 20px;
  padding-top: 20px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  @media (max-width: 565px) {
    font-size: 15px;
  }
  @media (max-width: 365px) {
    font-size: 12px;
  }
`;

const TotalText = styled.div`
  justify-self: flex-start;
  font-size: 16px;
  color: #777984;
  padding-bottom: 20px;
  padding-top: 20px;

  @media (max-width: 750px) {
    font-size: 15px;
  }
  @media (max-width: 515px) {
    font-size: 14px;
  }
`;

const PaymentTypeText = styled.div`
  justify-self: flex-start;
  font-size: 15px;
  color: #777984;
  padding-bottom: 20px;

  @media (max-width: 630px) {
    font-size: 14px;
  }
  @media (max-width: 500px) {
    font-size: 13px;
  }
`;

const PaymentTypeTextValue = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: #1f2021;
  justify-self: flex-end;
  padding-bottom: 20px;

  @media (max-width: 630px) {
    font-size: 14px;
  }
  @media (max-width: 500px) {
    font-size: 13px;
  }
`;

const TotalValue = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: #1f2021;
  justify-self: flex-end;
  padding-bottom: 20px;
  padding-top: 20px;
`;

const DollarSpan = styled.span`
  font-size: 16px;
  color: #777984;
`;

const ButtonSection = styled.section`
  padding: 40px 40px 40px 40px;
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 625px) {
    padding: 30px 20px 20px 20px;
  }
  @media (max-width: 375px) {
    padding: 30px 0px 20px 10px;
  }
`;

const CancelDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HelpButton = styled.button`
  width: 272px;
  height: 72px;
  border-radius: 36px;
  box-shadow: 0 9px 18px 0 rgba(31, 32, 33, 0.05);
  background-color: #ffffff;
  border: 0;
  outline: none;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  color: #39c876;
  &:active,
  &:hover {
    outline: none;
    border: 0;
  }

  @media (max-width: 565px) {
    width: 100%;
  }
`;

const CancelButton = styled.button`
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 2.25;
  letter-spacing: normal;
  text-align: center;
  border: 0;
  color: #1f2021;
  background-color: transparent;
  outline: none;
  &:active,
  &:selected,
  &:focus,
  &:hover {
    outline: none !important;
    border: 0 !important;
  }
  &:focus {
    outline: none !important;
    border: 0 !important;
  }

  @media (max-width: 500px) {
    font-size: 14px;
  }
`;

const HelpText = styled.div`
  font-size: 16px;
  display: flex;
  align-items: center;
  color: #777984;

  @media (max-width: 500px) {
    font-size: 14px;
  }
`;

const MediaLarge = styled.div`
  @media (max-width: 510px) {
    display: none;
  }
`;

const MediaSmall = styled.div`
  @media (min-width: 511px) {
    display: none;
  }
`;

const OtherOrderDiv = styled.div`
  left: 11%;
  width: 210px;
  height: 69px;
  border-radius: 36px;
  box-shadow: 0 9px 18px 0 rgba(31, 32, 33, 0.05);
  background-color: #ffffff;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  color: #ff9c1b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: absolute;
  top: 180px;

  @media (max-width: 1550px) {
    left: 7%;
  }
  @media (max-width: 1375px) {
    left: 4%;
  }
  @media (max-width: 1250px) {
    display: none;
  }
`;

const BottomOtherOrderContainer = styled.div`
  @media (min-width: 1251px) {
    display: none;
  }
  width: 100%;
  margin-bottom: 50px;
`;

const MediaOtherOrderDiv = styled.div`
  margin: 0 auto;
  width: 315px;
  height: 60px;
  border-radius: 36px;
  box-shadow: 0 9px 18px 0 rgba(31, 32, 33, 0.05);
  background-color: #ffffff;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  color: #ff9c1b;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 400px) {
    width: 265px;
  }
`;

const OrderInformation = ({ changeToOtherOrder }) => {
  const dispatch = useDispatch();
  const orderStatusDetails = useSelector(state => state.orderStatus.details);

  useEffect(() => {
    if (!orderStatusDetails.orderId) {
      changeToOtherOrder();
    }
  }, [orderStatusDetails.orderId]);

  return (
    <MainContainer>
      <CancelSellOrderModal
        cancelSellOrder={() =>
          dispatch(cancelSellOrderWithReload(orderStatusDetails.uuid))
        }
      />

      <OtherOrderDiv onClick={changeToOtherOrder}>
        Check another order
      </OtherOrderDiv>

      <InnerContainer>
        <TopSection>
          <Header>Your order #{orderStatusDetails.orderId}</Header>
          <DateP>
            {new Date(orderStatusDetails.datePurchased).toLocaleDateString()}
          </DateP>
        </TopSection>
        <BottomSection>
          <CustomerInfo>
            <SmallHeader>Customer information</SmallHeader>
            <CustomerDiv>
              <div>
                <Text>{orderStatusDetails.customer.name}</Text>
                <Text>{orderStatusDetails.customer.email}</Text>
              </div>
              <div>
                <Text>{orderStatusDetails.customer.street}</Text>
                <Text>
                  {orderStatusDetails.customer.city}{" "}
                  {orderStatusDetails.customer.state}{" "}
                  {orderStatusDetails.customer.postcode}{" "}
                  {orderStatusDetails.customer.country}
                </Text>
              </div>
            </CustomerDiv>
          </CustomerInfo>
          <StatusInfo>
            <SmallHeader>Status</SmallHeader>
            <StatusGrid>
              <PendingDiv>
                <StatusText>{orderStatusDetails.paymentStatus}</StatusText>
                {orderStatusDetails.statusText && (
                  <StatusMessage>
                    <InfoImage
                      src="https://cdn.cardcash.com/website/ui/elements/basic%20icons%20info.svg"
                      alt="icon"
                    />
                    {orderStatusDetails.statusText}
                  </StatusMessage>
                )}
              </PendingDiv>
            </StatusGrid>
          </StatusInfo>
          <CardSection>
            <div>
              <Card>
                <SmallHeader>Cards</SmallHeader>
                <MediaLarge>
                  <Facevalue>Gift card value</Facevalue>
                </MediaLarge>
                <MediaSmall>
                  <Facevalue>Card value</Facevalue>
                </MediaSmall>
                <OfferValue>
                  {orderStatusDetails.type === "sell" ? "Payout" : "Price"}
                </OfferValue>
              </Card>
              {orderStatusDetails.cards &&
                orderStatusDetails.cards.map((card, index) => {
                  const cardLength = orderStatusDetails.cards.length - 1;
                  return (
                    <Card key={index}>
                      <CardImageNameDiv
                        borderBottom={
                          index === cardLength
                            ? ""
                            : "rgba(119, 121, 132, 0.15) solid 1px"
                        }
                      >
                        <CardImage src={card.image} alt={card.merchant} />
                        <span>{card.merchant}</span>
                      </CardImageNameDiv>
                      <Facevalue
                        borderBottom={"rgba(119, 121, 132, 0.15) solid 1px"}
                      >
                        ${parseFloat(card.faceValue).toFixed(2)}
                      </Facevalue>
                      <OfferValue
                        borderBottom={"rgba(119, 121, 132, 0.15) solid 1px"}
                      >
                        ${parseFloat(card.cashValue).toFixed(2)}
                      </OfferValue>
                    </Card>
                  );
                })}
              {orderStatusDetails.paymentType.includes("eGift") ? (
                <PaymentGrid>
                  <div></div>
                  <TotalText>
                    Total {orderStatusDetails.paymentType} value
                  </TotalText>
                  <TotalValue>
                    <DollarSpan>$</DollarSpan>
                    {parseFloat(orderStatusDetails.amount).toFixed(2)}
                  </TotalValue>
                </PaymentGrid>
              ) : (
                <Card>
                  <div></div>
                  <TotalText>Total</TotalText>
                  <TotalValue>
                    <DollarSpan>$</DollarSpan>
                    {parseFloat(orderStatusDetails.amount).toFixed(2)}
                  </TotalValue>
                </Card>
              )}

              {!orderStatusDetails.paymentType.includes("eGift") && (
                <PaymentTypeGrid>
                  <div></div>
                  <PaymentTypeText>Payment type</PaymentTypeText>
                  <PaymentTypeTextValue>
                    {orderStatusDetails.paymentType}
                  </PaymentTypeTextValue>
                </PaymentTypeGrid>
              )}
            </div>
          </CardSection>
        </BottomSection>
        <ButtonSection>
          {!orderStatusDetails.canCancel && (
            <HelpText>Need help with your order?</HelpText>
          )}
          <div>
            <Link to="/faqs/">
              <HelpButton>Help</HelpButton>
            </Link>
          </div>
          {orderStatusDetails.canCancel && (
            <CancelDiv>
              <CancelButton
                onClick={() => {
                  dispatch(defaultAction(OPEN_CANCEL_SELL_ORDER_MODAL, true));
                }}
              >
                Cancel your order
              </CancelButton>
            </CancelDiv>
          )}
        </ButtonSection>
      </InnerContainer>
      <BottomOtherOrderContainer>
        <MediaOtherOrderDiv onClick={changeToOtherOrder}>
          Check another order
        </MediaOtherOrderDiv>
      </BottomOtherOrderContainer>
    </MainContainer>
  );
};

export default OrderInformation;
